import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc } from '../firestore-compat';
import '../App.css';

/**
 * Popup de notificações de recusas de convites
 * @param {Object} props
 * @param {boolean} props.isOpen - Se o popup está aberto
 * @param {Function} props.onClose - Callback para fechar o popup
 * @param {Function} props.onReagendar - Callback para reagendar evento (recebe eventoId e collection)
 */
function NotificationsPopup({ isOpen, onClose, onReagendar }) {
    const [notificacoes, setNotificacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const currentUser = auth.currentUser;

    useEffect(() => {
        if (!isOpen || !currentUser) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Escutar notificações em tempo real
        const q = query(
            collection(db, 'notificacoes'),
            where('destinatarioUid', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notifs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Ordenar por data (mais recentes primeiro)
            notifs.sort((a, b) => {
                const dateA = new Date(a.dataNotificacao);
                const dateB = new Date(b.dataNotificacao);
                return dateB - dateA;
            });

            setNotificacoes(notifs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [isOpen, currentUser]);

    const handleMarcarLida = async (notifId) => {
        try {
            await updateDoc(doc(db, 'notificacoes', notifId), {
                lida: true
            });
        } catch (error) {
            console.error('Erro ao marcar notificação como lida:', error);
        }
    };

    const handleExcluirNotificacao = async (notifId) => {
        if (!window.confirm('Deseja excluir esta notificação?')) return;

        try {
            await deleteDoc(doc(db, 'notificacoes', notifId));
        } catch (error) {
            console.error('Erro ao excluir notificação:', error);
            alert('Erro ao excluir notificação.');
        }
    };

    const handleReagendar = (notif) => {
        if (onReagendar) {
            onReagendar(notif.eventoId, notif.eventoCollection);
            onClose();
        }
    };

    if (!isOpen) return null;

    const notificacoesNaoLidas = notificacoes.filter(n => !n.lida);
    const notificacoesLidas = notificacoes.filter(n => n.lida);

    return (
        <div className="modal-overlay" style={{ zIndex: 3000 }} onClick={onClose}>
            <div
                className="modal-content modal-lg"
                style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #e67e22, #d35400)',
                    color: '#fff',
                    padding: '20px',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        🔔 Minhas Notificações
                        {notificacoesNaoLidas.length > 0 && (
                            <span style={{
                                backgroundColor: '#dc3545',
                                borderRadius: '50%',
                                padding: '4px 8px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                            }}>
                                {notificacoesNaoLidas.length}
                            </span>
                        )}
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#fff',
                            fontSize: '24px',
                            cursor: 'pointer',
                            padding: '0',
                            lineHeight: '1'
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Body */}
                <div style={{
                    padding: '20px',
                    overflowY: 'auto',
                    flex: 1,
                    backgroundColor: '#f8f9fa'
                }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : notificacoes.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '2px dashed #ddd'
                        }}>
                            <div style={{ fontSize: '3rem', marginBottom: '10px' }}>✅</div>
                            <h4 style={{ color: '#666', marginBottom: '5px' }}>Nenhuma notificação</h4>
                            <p style={{ color: '#999', margin: 0 }}>Você não possui notificações no momento.</p>
                        </div>
                    ) : (
                        <>
                            {/* Notificações Não Lidas */}
                            {notificacoesNaoLidas.length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{
                                        color: '#333',
                                        fontSize: '14px',
                                        textTransform: 'uppercase',
                                        marginBottom: '10px',
                                        fontWeight: '600'
                                    }}>
                                        Novas ({notificacoesNaoLidas.length})
                                    </h4>
                                    {notificacoesNaoLidas.map(notif => (
                                        <NotificationCard
                                            key={notif.id}
                                            notif={notif}
                                            onMarcarLida={handleMarcarLida}
                                            onExcluir={handleExcluirNotificacao}
                                            onReagendar={handleReagendar}
                                            isUnread={true}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Notificações Lidas */}
                            {notificacoesLidas.length > 0 && (
                                <div>
                                    <h4 style={{
                                        color: '#666',
                                        fontSize: '14px',
                                        textTransform: 'uppercase',
                                        marginBottom: '10px',
                                        fontWeight: '600'
                                    }}>
                                        Anteriores ({notificacoesLidas.length})
                                    </h4>
                                    {notificacoesLidas.map(notif => (
                                        <NotificationCard
                                            key={notif.id}
                                            notif={notif}
                                            onMarcarLida={handleMarcarLida}
                                            onExcluir={handleExcluirNotificacao}
                                            onReagendar={handleReagendar}
                                            isUnread={false}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Card individual de notificação
 */
function NotificationCard({ notif, onMarcarLida, onExcluir, onReagendar, isUnread }) {
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isChangeNotification = notif.tipo === 'alteracao_agendamento';

    return (
        <div style={{
            backgroundColor: isUnread ? '#fff' : '#f8f9fa',
            border: isUnread
                ? (isChangeNotification ? '2px solid #00897b' : '2px solid #e67e22')
                : '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '12px',
            boxShadow: isUnread
                ? (isChangeNotification ? '0 2px 8px rgba(0, 80, 157, 0.1)' : '0 2px 8px rgba(230, 126, 34, 0.1)')
                : '0 1px 3px rgba(0,0,0,0.05)',
            position: 'relative'
        }}>
            {/* Badge de não lida */}
            {isUnread && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: isChangeNotification ? '#00897b' : '#dc3545',
                    color: '#fff',
                    fontSize: '10px',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                }}>
                    {isChangeNotification ? 'ALTERAÇÃO' : 'Recusa'}
                </div>
            )}

            {/* Informações do Evento */}
            <div style={{ marginBottom: '12px' }}>
                <h5 style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '16px',
                    fontWeight: '600',
                    paddingRight: '60px' // Espaço para a badge
                }}>
                    {notif.eventoTitulo || notif.eventoDescricao}
                </h5>

                {isChangeNotification ? (
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                        🚀 Este evento foi reagendado por <strong>{notif.alteradoPor}</strong>
                    </div>
                ) : (
                    <>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                            <strong>📅</strong> {new Date(notif.eventoData + 'T00:00:00').toLocaleDateString('pt-BR')} às {notif.eventoHorario}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                            <strong>⏰</strong> Recusado em: {formatDate(notif.dataNotificacao)}
                        </div>
                    </>
                )}
            </div>

            {/* Detalhes específicos por tipo */}
            {isChangeNotification ? (
                <div style={{
                    backgroundColor: '#e0f2f1',
                    borderLeft: '4px solid #00897b',
                    borderRadius: '4px',
                    padding: '12px',
                    marginBottom: '12px'
                }}>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <div style={{ flex: 1, textDecoration: 'line-through', color: '#999', fontSize: '12px' }}>
                            <strong>DE:</strong> {new Date(notif.dataAntiga + 'T00:00:00').toLocaleDateString('pt-BR')} às {notif.horarioAntigo}
                        </div>
                        <div style={{ fontSize: '18px', color: '#00897b' }}>➔</div>
                        <div style={{ flex: 1, color: '#00897b', fontWeight: 'bold' }}>
                            <strong>PARA:</strong> {new Date(notif.dataNova + 'T00:00:00').toLocaleDateString('pt-BR')} às {notif.horarioNovo}
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{
                    backgroundColor: isUnread ? '#fff3cd' : '#f1f1f1',
                    border: '1px solid #ffc107',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '12px'
                }}>
                    <div style={{ fontSize: '13px', marginBottom: '6px' }}>
                        <strong style={{ color: '#856404' }}>❌ Recusado por:</strong> {notif.recusadoPorNome}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                        <strong>Justificativa:</strong> "{notif.justificativa}"
                    </div>
                </div>
            )}

            {/* Ações */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {!isChangeNotification && (
                    <button
                        onClick={() => onReagendar(notif)}
                        style={{
                            backgroundColor: '#28a745',
                            color: '#fff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                    >
                        📅 Reagendar
                    </button>
                )}

                {isUnread && (
                    <button
                        onClick={() => onMarcarLida(notif.id)}
                        style={{
                            backgroundColor: isChangeNotification ? '#00897b' : '#6c757d',
                            color: '#fff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isChangeNotification ? '#003d7a' : '#5a6268'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isChangeNotification ? '#00897b' : '#6c757d'}
                    >
                        {isChangeNotification ? '✓ Ciente' : '✓ Marcar como lida'}
                    </button>
                )}

                <button
                    onClick={() => onExcluir(notif.id)}
                    style={{
                        backgroundColor: 'transparent',
                        color: '#dc3545',
                        border: '1px solid #dc3545',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#dc3545';
                        e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#dc3545';
                    }}
                >
                    🗑️ Excluir
                </button>
            </div>
        </div>
    );
}

export default NotificationsPopup;
